import { configAnalytics, errorForm, logger, navigation } from '@origin-digital/reporting-client';
import { get } from 'lodash';
import { ERROR_CODES } from 'ts/passwordless-login/components/pages/error/ErrorCodes';
import { getDeviceId } from '../url/UrlUtils';
import { getWindow } from '../window/Window';

const initialise = () => {
  configAnalytics({
    appName: 'login',
    debugToConsole: false,
  });
};

const fireNavigationEvent = (uri: string) => {
  navigation('screen', {
    currentUri: location.href,
    friendlyUri: uri,
  });
};

const fireErrorEvent = (code, message) => {
  errorForm({
    errorCode: code,
    errorMessage: message,
    formName: 'login',
  });
};

const loginSuccessAlert = (userLoginType: string, businessPartnerId: string) => {
  if (getWindow()._satellite) {
    getWindow()._satellite.track('login', {
      businessPartner: businessPartnerId,
      loginMethod: userLoginType,
    });
  } else {
    logger.debug('analytics library not loaded', { uniqueId: getDeviceId() });
  }
};

// error code to analytics error code mapping
const errorCodeToAnalyticsMap = {
  invalid_user_password: 'my-account:invalid_login_credentials',
  auth0_blocked: 'my-account:login_ip_blocked',
  too_many_attempts: 'my-account:login_max_attempts',
  password_leaked: 'my-account:login_password_compromised',
  generic_error: 'my-account:unknown_login_error',
  auth0_timeout: 'my-account:login_timeout_error',
  authcallback_generic_error: 'my-account:authcallback_generic_error',
  authcallback_aem_calls_failed: 'my-account:authcallback_aem_calls_failed',
  authcallback_auth0_call_failed: 'my-account:authcallback_auth0_call_failed',
  authcallback_authtoken_api_failed: 'my-account:authcallback_authtoken_api_failed',
  authcallback_user_session_api_failed: 'my-account:authcallback_user_session_api_failed',
  authcallback_auth0_configurations_not_provided:
    'my-account:authcallback_auth0_configurations_not_provided',
  authcallback_not_allowed_domain_found: 'my-account:authcallback_not_allowed_domain_found',
  auth0_decode_hash: 'my-account:auth0_decode_hash',
  [ERROR_CODES.FORGOT_EMAIL_3_POINT_CHECK]: '3-point-check-error',
  [ERROR_CODES.FORGOT_EMAIL_ACCOUNT_SEARCH]: 'account-search-error',
  [ERROR_CODES.MANUAL_REG_3_POINT_CHECK]: '3-point-check-error',
  [ERROR_CODES.MANUAL_REG_ACCOUNT_SEARCH]: 'account-search-error',
  [ERROR_CODES.MANUAL_REG_OTC_ERROR]: 'manual-reg-cannot-send-otc',
  [ERROR_CODES.MANUAL_REG_SAP_EMAIL_ERROR]: 'manual-reg-sap-email-error',
  [ERROR_CODES.MANUAL_REG_SAP_CREATE_ERROR]: 'manual-reg-create-user-error',
};

// error code to analytics error message mapping
const errorCodeToErrorMessageAnalyticsMap = {
  invalid_user_password: 'my-account:invalid_login_credentials',
  auth0_blocked: 'my-account:login_ip_blocked',
  too_many_attempts: 'my-account:login_max_attempts',
  password_leaked: 'my-account:login_password_compromised',
  generic_error: 'my-account:unknown_login_error',
  auth0_timeout: 'my-account:login_timeout_error',
  authcallback_generic_error: 'my-account:authcallback_generic_error',
  authcallback_aem_calls_failed: 'my-account:authcallback_aem_calls_failed',
  authcallback_auth0_call_failed: 'my-account:authcallback_auth0_call_failed',
  authcallback_authtoken_api_failed: 'my-account:authcallback_authtoken_api_failed',
  authcallback_user_session_api_failed: 'my-account:authcallback_user_session_api_failed',
  authcallback_auth0_configurations_not_provided:
    'my-account:authcallback_auth0_configurations_not_provided',
  authcallback_not_allowed_domain_found: 'my-account:authcallback_not_allowed_domain_found',
  auth0_decode_hash: 'my-account:auth0_decode_hash',
  [ERROR_CODES.FORGOT_EMAIL_3_POINT_CHECK]: 'my-account:forgotemail_error',
  [ERROR_CODES.FORGOT_EMAIL_ACCOUNT_SEARCH]: 'my-account:forgotemail_error',
  [ERROR_CODES.MANUAL_REG_3_POINT_CHECK]: 'my-account:auth:manual-reg-error',
  [ERROR_CODES.MANUAL_REG_ACCOUNT_SEARCH]: 'my-account:auth:manual-reg-error',
  [ERROR_CODES.MANUAL_REG_OTC_ERROR]: 'my-account:auth:manual-reg-error',
  [ERROR_CODES.MANUAL_REG_SAP_EMAIL_ERROR]: 'my-account:auth:manual-reg-error',
  [ERROR_CODES.MANUAL_REG_SAP_CREATE_ERROR]: 'my-account:auth:manual-reg-error',
};

const errorCodeToAnalyticsErrorCode = (errorCode: string): string => {
  return get(errorCodeToAnalyticsMap, errorCode, `my-account:${errorCode}`);
};

const errorCodeToAnalyticsErrorMessage = (errorCode: string): string => {
  return get(errorCodeToErrorMessageAnalyticsMap, errorCode, `my-account:${errorCode}`);
};

export {
  initialise,
  fireNavigationEvent,
  fireErrorEvent,
  loginSuccessAlert,
  errorCodeToAnalyticsErrorCode,
  errorCodeToAnalyticsErrorMessage,
};
